import { TextFieldModule } from '@angular/cdk/text-field';
import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { UntilDestroy } from '@ngneat/until-destroy';
import _partition from 'lodash.partition';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';

import { StringHelper } from 'tiime-expert-utils';
import {
  DatepickerHeaderComponent,
  TiimeAutocompleteModule,
  TiimeButtonModule,
  TiimeDatepickerModule,
  TiimeFormModule,
  TiimeIconModule,
  TiimeInputContainerModule,
  TiimeSnackbarModule,
  TiimeSnackbarService,
  TiimeTagModule,
  TiimeTooltipModule
} from 'tiime-material';

import { BusinessUserApiService } from '@api-services/business-user-api.service';
import { GIFileType } from '@enums/gi-file-type.enum';
import { ContractApiService } from '@gi/api/services/contract-api.service';
import { ContractAnnexLineCharge } from '@gi/enums/contract-annex-line-charge.enum';
import { ContractForm } from '@gi/forms/contract-form';
import { ContractAnnex } from '@gi/models/contract-annex';
import { ContractAnnexModel } from '@gi/models/contract-annex-model';
import { Prestation } from '@gi/models/prestation';
import { FileHelper } from '@helpers/file.helper';
import { FormHelper } from '@helpers/form.helper';
import { shareReplay1 } from '@helpers/rxjs.helper';
import { Brand } from '@models/brand';
import { BusinessUnit } from '@models/business-unit';
import { CodeAndLabel } from '@models/code-and-label';
import { ModelOffer } from '@models/model-offer';
import { Partner } from '@models/partner';
import { TimeTrackingsTask } from '@models/time-trackings-task';
import { GICachedDataApiService } from '@modules/domains/gi/api/services/gi-cached-data-api.service';
import { ContractAnnexLineFormTableComponent } from '@modules/shared/tables/contract-annex-line-form-table/contract-annex-line-form-table.component';
import { ContractFilesFormTableComponent } from '@modules/shared/tables/contract-files-form-table/contract-files-form-table.component';
import { PrestationsFormTableComponent } from '@modules/shared/tables/prestations-form-table/prestations-form-table.component';
import { AnnexModelAutocompleteComponent } from '@shared-autocompletes/annex-model-autocomplete/annex-model-autocomplete.component';
import { OfferAutocompleteComponent } from '@shared-autocompletes/offer-autocomplete/offer-autocomplete.component';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogModule } from '@shared-dialogs/confirm-dialog/confirm-dialog.module';
import { ConfirmDialogService } from '@shared-dialogs/confirm-dialog/confirm-dialog.service';
import { SafePipe } from '@shared-pipes/safe/safe.pipe';
import { BusinessUnitSelectComponent } from '@shared-selectors/business-unit-select/business-unit-select.component';
import { ContractBrandSelectComponent } from '@shared-selectors/contract-brand-select/contract-brand-select.component';
import { ContributorSelectComponent } from '@shared-selectors/contributor-select/contributor-select.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-contract-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TiimeInputContainerModule,
    TiimeIconModule,
    TiimeButtonModule,
    TiimeTooltipModule,
    TiimeSnackbarModule,
    TextFieldModule,
    TiimeDatepickerModule,
    PrestationsFormTableComponent,
    ContractFilesFormTableComponent,
    TiimeFormModule,
    TiimeAutocompleteModule,
    OfferAutocompleteComponent,
    AnnexModelAutocompleteComponent,
    ContributorSelectComponent,
    ConfirmDialogModule,
    ContractBrandSelectComponent,
    BusinessUnitSelectComponent,
    AsyncPipe,
    ContractAnnexLineFormTableComponent,
    TiimeTagModule
  ],
  templateUrl: './contract-form.component.html',
  styleUrls: ['./contract-form.component.scss']
})
export class ContractFormComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) contractForm: ContractForm;
  @Input({ required: true }) modelOffers: ModelOffer[] = [];
  @Input({ required: true }) annexModels: ContractAnnexModel[] = [];
  @Input({ required: true }) tasks: TimeTrackingsTask[] = [];
  @Input({ required: true }) partner: Partner;
  @Input({ required: true }) businessUnits: BusinessUnit[];
  @Input({ required: true }) contractAnnexLineTypes: CodeAndLabel<string>[] = [];
  @Input({ required: true }) contractAnnexLineCharges: CodeAndLabel<ContractAnnexLineCharge>[] = [];

  @Output() readonly downloadFile: EventEmitter<number> = new EventEmitter<number>();
  @Output() readonly openFileInNewTab: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild(PrestationsFormTableComponent) prestationsFormTableComponent: PrestationsFormTableComponent;
  @ViewChild('startDateInput') startDateInput: ElementRef<HTMLInputElement>;

  readonly tiimeDatepickerHeader = DatepickerHeaderComponent;
  readonly filteredModelOffers: WritableSignal<ModelOffer[]> = signal<ModelOffer[]>([]);
  readonly searchModelOfferControl: FormControl<string> = new FormControl<string>('');
  readonly filteredAnnexModels: WritableSignal<ContractAnnexModel[]> = signal<ContractAnnexModel[]>([]);
  readonly searchAnnexModelControl: FormControl<string> = new FormControl<string>('');
  readonly acceptedTypes: string[] = ['application/pdf'];
  readonly contractFileTypes$: Observable<CodeAndLabel<GIFileType>[]> = this.gICachedDataApiService.getFileTypes();
  readonly contractCommercialBrands$: Observable<Brand[]> = this.businessUserApiService
    .getBusinessUserPartnerCommercialBrands()
    .pipe(
      map((brands: Brand[]) => brands.filter((brand: Brand) => !brand.archivedAt)),
      shareReplay1()
    );
  readonly contractBusinessUnitBrand: WritableSignal<Brand> = signal<Brand>(null);
  readonly logoLoading: WritableSignal<boolean> = signal<boolean>(false);
  readonly logoUrl: WritableSignal<SafeResourceUrl> = signal<SafeResourceUrl>(null);

  private readonly subscription: Subscription = new Subscription();

  constructor(
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly contractApiService: ContractApiService,
    private readonly businessUserApiService: BusinessUserApiService,
    private readonly domSanitizer: DomSanitizer,
    private readonly snackbar: TiimeSnackbarService,
    private readonly gICachedDataApiService: GICachedDataApiService
  ) {}

  ngOnInit(): void {
    this.observeSearchModelOffer();
    this.observeSearchAnnexModel();
    this.observeContractForm();
    this.initContractBusinessUnitBrand();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.startDateInput?.nativeElement.focus());
  }

  addNewPrestation(): void {
    const prestation = new Prestation();
    this.contractForm.addPrestation(prestation);
    setTimeout(() => this.prestationsFormTableComponent?.openLastTaskSelectPanel());
  }

  removePrestation(index: number): void {
    this.contractForm.removePrestation(index);
  }

  removeModelOffer(modelOfferId: number): void {
    this.contractForm.removeModelOffer(modelOfferId);
  }

  removeAnnex(index: number): void {
    this.contractForm.removeAnnex(index);
  }

  removeFile(index: number): void {
    this.contractForm.removeFile(index);
  }

  onSelectModelOffer(offer: ModelOffer): void {
    this.resetSearchModelOfferControl();
    this.canSelectModelOffer(offer)
      .pipe(
        filter((canSelectOffer: boolean) => canSelectOffer),
        tap(() => {
          this.contractForm.addPrestationsFromModelOffer(offer);
          this.contractForm.addFilesFromModelOffer(offer);
          this.contractForm.addAnnexesFromModelOffer(offer);
        })
      )
      .subscribe();
  }

  onSelectAnnexModel(annexModel: ContractAnnexModel): void {
    this.resetSearchAnnexModelControl();
    const contractAnnex = ContractAnnex.fromContractAnnexModel(annexModel);
    this.contractForm.addAnnex(contractAnnex);
  }

  onSearchModelOffer(search: string): void {
    if (!search || typeof search !== 'string') {
      this.filteredModelOffers.set(this.modelOffers?.filter((offer: ModelOffer) => !offer.archivedAt));
      return;
    }

    this.filteredModelOffers.set(
      this.modelOffers.filter(
        (offer: ModelOffer) =>
          !offer.archivedAt &&
          (StringHelper.normalizeToLowerCase(offer.label ?? '').includes(StringHelper.normalizeToLowerCase(search)) ||
            StringHelper.normalizeToLowerCase(offer.tag ?? '').includes(StringHelper.normalizeToLowerCase(search)))
      )
    );
  }

  onSearchAnnexModel(search: string): void {
    if (!search || typeof search !== 'string') {
      this.filteredAnnexModels.set(
        this.annexModels?.filter((annexModel: ContractAnnexModel) => !annexModel.archivedAt)
      );
      return;
    }

    this.filteredAnnexModels.set(
      this.annexModels.filter(
        (annexModel: ContractAnnexModel) =>
          !annexModel.archivedAt &&
          StringHelper.normalizeToLowerCase(annexModel.label ?? '').includes(StringHelper.normalizeToLowerCase(search))
      )
    );
  }

  emitDownloadFile(fileId: number): void {
    this.downloadFile.emit(fileId);
  }

  emitOpenFileInNewTab(fileId: number): void {
    this.openFileInNewTab.emit(fileId);
  }

  importFile(fileSelector: HTMLInputElement): void {
    fileSelector.value = '';
    fileSelector.click();
  }

  onFileSelected(fileList: FileList): void {
    const files = Array.from(fileList);
    const partition = _partition(files, (file: File) => FileHelper.isValid(file, this.acceptedTypes));
    const acceptedFiles: File[] = partition[0];
    const invalidFiles: File[] = partition[1];

    if (invalidFiles?.length) {
      this.snackbar.openError(FileHelper.getInvalidFileErrorMessage(this.acceptedTypes));
    }

    Array.from(acceptedFiles).forEach((file: File) => this.contractForm.addFileToUpload(file));
  }

  businessUnitChange(businessUnit: BusinessUnit): void {
    this.setContractBusinessUnitBrand(businessUnit)
      .pipe(
        tap((brand: Brand) =>
          this.contractForm.patchValue({
            brand: brand.name,
            logoFileId: brand.logoFileId
          })
        )
      )
      .subscribe();
  }

  brandChange(brand: Brand): void {
    this.contractForm.logoFileId.setValue(brand.logoFileId);
  }

  private canSelectModelOffer(offer: ModelOffer): Observable<boolean> {
    const contractHasSameOffer = this.contractForm.prestations.controls.some(
      prestationForm => prestationForm.contractOffer?.value?.offer.id === offer.id
    );
    if (!contractHasSameOffer) {
      return of(true);
    }

    return this.confirmDialogService
      .open({
        data: new ConfirmDialogData(
          'Attention',
          `Ce modèle d'offre est déjà présent sur le contrat, êtes-vous sûr de vouloir continuer ?`,
          'Ajouter',
          'Annuler',
          null,
          'accent'
        )
      })
      .afterClosed();
  }

  private resetSearchModelOfferControl(): void {
    this.searchModelOfferControl.reset();
  }

  private resetSearchAnnexModelControl(): void {
    this.searchAnnexModelControl.reset();
  }

  private observeSearchModelOffer(): void {
    this.subscription.add(
      this.searchModelOfferControl.valueChanges
        .pipe(
          debounceTime(200),
          tap((search: string) => this.onSearchModelOffer(search))
        )
        .subscribe()
    );
  }

  private observeSearchAnnexModel(): void {
    this.subscription.add(
      this.searchAnnexModelControl.valueChanges
        .pipe(
          debounceTime(200),
          tap((search: string) => this.onSearchAnnexModel(search))
        )
        .subscribe()
    );
  }

  private observeContractForm(): void {
    this.subscription.add(
      FormHelper.getValueChange(this.contractForm)
        .pipe(
          tap(() => {
            this.contractForm.prestations.disabled
              ? this.searchModelOfferControl.disable()
              : this.searchModelOfferControl.enable();
            this.contractForm.annexes.disabled
              ? this.searchAnnexModelControl.disable()
              : this.searchAnnexModelControl.enable();
          })
        )
        .subscribe()
    );

    this.subscription.add(
      FormHelper.getValueChange(this.contractForm.logoFileId)
        .pipe(
          tap(() => this.logoUrl.set(null)),
          filter((logoFileId: string) => !!logoFileId),
          tap((logoFileId: string) => this.getLogoPreview(logoFileId))
        )
        .subscribe()
    );
  }

  private initContractBusinessUnitBrand(): void {
    this.subscription.add(
      FormHelper.getValueChange<BusinessUnit>(this.contractForm.businessUnit)
        .pipe(
          take(1),
          filter((businessUnit: BusinessUnit) => !!businessUnit),
          switchMap((businessUnit: BusinessUnit) => this.setContractBusinessUnitBrand(businessUnit))
        )
        .subscribe()
    );
  }

  private setContractBusinessUnitBrand(businessUnit: BusinessUnit): Observable<Brand> {
    return this.businessUserApiService.getBusinessUserPartnerBusinessUnit(businessUnit.id).pipe(
      map((businessUnit: BusinessUnit) => {
        const brand: Brand = new Brand(null, businessUnit.brand, businessUnit.logoFileId);

        this.contractBusinessUnitBrand.set(brand);
        return brand;
      })
    );
  }

  private getLogoPreview(logoFileId: string): void {
    this.logoLoading.set(true);

    this.contractApiService
      .getBrandLogoFilePreview(logoFileId)
      .pipe(
        tap((blobPreview: Blob) =>
          this.logoUrl.set(new SafePipe(this.domSanitizer).transform(URL.createObjectURL(blobPreview), 'resourceUrl'))
        ),
        finalize(() => this.logoLoading.set(false))
      )
      .subscribe();
  }
}
